import { NOL_PASS_PRODUCT_TAG, NOL_PRODUCT_CODE } from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { ITransformedMeal } from "../../Orchestrator/Types/Interface/IAddOns";
import { INolProduct } from "../../Orchestrator/Types/Interface/INol";
import { IOffer } from "../../Orchestrator/Types/Interface/IOffer";
import { IFmLmOffersResponse } from "../../Orchestrator/Types/Types/OffersResponse";
import { IBookingResponse } from "../../Orchestrator/Types/Index";
import { calculatePrice, isInfantPassenger } from "./validationUtil";

/** True when a transformed addon is a NOL product (code prefix on `type`). */
export const isNolMeal = (meal: ITransformedMeal): boolean =>
  (meal?.type || "").toUpperCase().startsWith(NOL_PRODUCT_CODE);

/** True when any option in a category is NOL — used to drop NOL from Extras. */
export const isNolCategoryOptions = (options: ITransformedMeal[]): boolean =>
  (options || []).some(isNolMeal);

/**
 * The NOL pass product on an offer from /fmlm-offers, if any.
 *
 * Selected by product TAG. Neither of the earlier candidates works:
 * `travelPassType` (the documented marker) has never appeared on the wire, and
 * the product code is per-variant ("TEST4_gold"), while the generic
 * "FMLM travel pass" that shares the stop must NOT be offered as a NOL card.
 */
export const nolPassProduct = (
  products: { code?: string; description?: string; summary?: string; productTags?: string[] }[] | undefined
) =>
  (products || []).find((p) =>
    (p?.productTags || []).some((tag) => (tag || "").toUpperCase() === NOL_PASS_PRODUCT_TAG)
  );

const journeyCardProduct = (offer: IOffer) => nolPassProduct(offer?.products);

/** Per-passenger price of an FMLM offer, in major units. */
const offerPrice = (offer: IOffer): number =>
  calculatePrice(
    (offer?.admissionOfferParts || [])[0]?.price || offer?.offerSummary?.minimalPrice
  ) || 0;

/**
 * Distil GET /bookings/{id}/fmlm-offers down to the purchasable NOL variants,
 * keyed by leg id.
 *
 * Replaces the old route, which read NOL out of the ADDONS payload
 * (additional-offers) and recognised it by product-code prefix. NOL is an
 * end-to-end journey product, not an addon: it is selected here by the
 * NOL_PASS product tag, and the addons payload is left alone for real addons.
 *
 * The response is grouped by passenger, so the same variant appears once per
 * passenger. Variants are merged by product code per leg, retaining each
 * passenger's own offer id (see INolProduct.offerIdByPassengerRef).
 */
export const nolProductsFromFmlmOffers = (
  response?: IFmLmOffersResponse | null
): Record<string, INolProduct[]> => {
  const byLeg: Record<string, INolProduct[]> = {};

  for (const passengerOffer of response?.passengerFmLmOffers || []) {
    const passengerRef = passengerOffer?.passengerExternalRef || "";
    for (const legOffer of passengerOffer?.fmLmLegOffers || []) {
      const legId = legOffer?.legId || "";
      if (!legId) continue;
      for (const offer of legOffer?.offers || []) {
        const product = journeyCardProduct(offer);
        if (!product) continue;

        const list = byLeg[legId] || (byLeg[legId] = []);
        const code = product.code || "";
        // Keyed by stop as well as product code: /fmlm-offers returns ONE entry
        // per stop of the SAME leg (observed on INT: legId 947b9d9f… appears
        // twice, "Abu Dhabi Station"/PMB and "Dubai Station"/PDW, same FMLM
        // product at the same price). Keying on code alone merges the two ends
        // of the journey into one row and loses the side the pass belongs to.
        const stopRef = (offer as any)?.fmLmStopReference || "";
        const existing = list.find((p) => p.type === code && p.stopRef === stopRef);
        if (existing) {
          if (passengerRef && !existing.passengerRefs.includes(passengerRef)) {
            existing.passengerRefs.push(passengerRef);
          }
          if (passengerRef) existing.offerIdByPassengerRef[passengerRef] = offer.offerId;
          continue;
        }
        list.push({
          type: code,
          label: product.description || product.summary || code,
          price: offerPrice(offer),
          offerId: offer.offerId,
          legId,
          tripId: legOffer.tripId || "",
          stopRef,
          stopName: legOffer.stopName || "",
          passengerRefs: passengerRef ? [passengerRef] : [],
          offerIdByPassengerRef: passengerRef ? { [passengerRef]: offer.offerId } : {},
        });
      }
    }
  }

  return byLeg;
};

/**
 * NOL variants available for a leg, optionally narrowed to one end of it.
 *
 * `stopRef` is the station the pass is bought for — `trainLeg.origin`/
 * `destination.stopPlaceRef`, which matches the offer's `fmLmStopReference`
 * (both "PMB"/"PDW" on INT). Pass it from any surface that renders a single
 * side, or the Dubai row also lists the Abu Dhabi pass.
 */
export const nolProductsForLeg = (
  nolOffers: Record<string, INolProduct[]> | undefined,
  legId: string,
  stopRef?: string
): INolProduct[] => {
  if (!legId) return [];
  const forLeg = nolOffers?.[legId] || [];
  return stopRef ? forLeg.filter((p) => p.stopRef === stopRef) : forLeg;
};

/**
 * Every NOL variant sold at `stopRef`, across whichever legs the offers name.
 *
 * Change-date needs this: the checkout card is bound to the leg of the NEWLY
 * selected train (selectedTrainLegsAtom), while `/bookings/{id}/fmlm-offers`
 * keys its answer to the legs the BOOKING currently holds — the original ones.
 * The two id namespaces never meet, so a by-leg read finds nothing and the Nol
 * row cannot render. The station is what survives a change of date, exactly as
 * it does for the shuttle's origin/destination matching.
 *
 * Deduped by offer id: the same variant appears once per leg it is sold on.
 */
export const nolProductsForStop = (
  nolOffers: Record<string, INolProduct[]> | undefined,
  stopRef: string
): INolProduct[] => {
  if (!stopRef) return [];
  const seen = new Set<string>();
  const products: INolProduct[] = [];
  for (const forLeg of Object.values(nolOffers || {})) {
    for (const product of forLeg) {
      if (product.stopRef !== stopRef) continue;
      const key = product.offerId || `${product.type}|${product.price}`;
      if (seen.has(key)) continue;
      seen.add(key);
      products.push(product);
    }
  }
  return products;
};

/**
 * Youngest age that may hold a Nol pass. Under this, no pass is sold: infants
 * travel on a guardian's lap, and Dubai's Nol day pass is not issued to a child
 * under 5 (they travel free on the network).
 */
export const NOL_MIN_PASSENGER_AGE = 5;

/**
 * May this passenger be offered a Nol pass?
 *
 * Both exclusions are needed rather than either alone: an INFANT is identified
 * by its discount card as well as its age band, while a young CHILD carries no
 * card and is caught only by `age`. A passenger with no age at all is treated as
 * eligible — that is the adult default everywhere else, and blocking on missing
 * data would silently drop paying passengers from the list.
 */
export const canBuyNolPass = (passenger: any): boolean => {
  if (isInfantPassenger(passenger)) return false;
  const age = passenger?.age;
  return !(typeof age === "number" && age < NOL_MIN_PASSENGER_AGE);
};

/**
 * True when a booked ancillary is a NOL day-pass — i.e. belongs to the
 * End-to-end Journey flow, not the "Extras" (meals/lounge/addon-shuttle)
 * surfaces. Resolves the product code the robust way: the reservation product
 * only carries `productId`, so the code lives on the OFFER's product list
 * (`products[].code`). Falls back to the raw ancillary `type` for wire shapes
 * that don't join cleanly. Pass the products from the offer that owns `anc`
 * (or a merged list when the product may live on a sibling offer).
 *
 * This is the single source of truth for NOL-ancillary detection — do not
 * re-inline `products[0].productCode || type` at call sites (that operand is
 * always undefined on the real shape, leaving detection on `type` alone).
 */
export const isNolAncillary = (
  offerProducts: { id?: string; code?: string }[] | undefined,
  anc: any
): boolean => {
  const ancProduct = (anc?.products || [])[0];
  // The product code can live in either place depending on the backend flow:
  // directly on the ancillary's product (`productCode`, typed-away but present
  // at runtime) or on the owning offer's product list (`code`, joined by
  // productId). Check both, then fall back to the raw ancillary `type`.
  const offerCode = (offerProducts || []).find((p) => p?.id === ancProduct?.productId)?.code;
  const code = ancProduct?.productCode || offerCode || anc?.type || "";
  return String(code).toUpperCase().startsWith(NOL_PRODUCT_CODE);
};

/**
 * True when a booked ADMISSION is an FMLM travel pass (the shape
 * add-fmlm-travel-passes produces). Passes booked through the old ancillary
 * route are matched by isNolAncillary instead — both are live on real bookings,
 * so every booking-side recognizer must accept either.
 */
export const isNolAdmission = (adm: any): boolean => adm?.isFmLmTravelPass === true;

/**
 * The pass's variant as a passenger reads it: "Gold", "Regular".
 *
 * The product description is the only field naming the variant — the code is
 * per-variant but machine-shaped ("TEST4_gold"), which is what a card printed
 * whenever the name could not be resolved. Some descriptions carry the "Day
 * Pass - " prefix and some do not, so it is dropped once here rather than at
 * each of the five surfaces that render a variant — the cards and the modal,
 * which already label the line "1 day pass", and the payment/cancel summary
 * rows, which read "1 Gold nol One-Day Pass" — count + this name, no noun of
 * their own (see [[e2eNolCardsInShort]]).
 */
export const nolVariantName = (description: string | undefined | null): string => {
  const raw = String(description || "").trim();
  // Only the prefixes actually seen on the wire: "Gold", "Day Pass - Gold",
  // "NOL Day Pass — Gold, All Zones" (hyphen, en dash and em dash all in play).
  // Deliberately NOT a leading `.*?` — that also ate the variant out of a
  // name-first "Gold Day Pass - All Zones", which reads as a wrong label rather
  // than a visible fallback. A description that is ONLY the prefix keeps its
  // raw text instead of reducing to nothing.
  //
  // English-only, deliberately: `Accept-Language` reaches the offers call, so an
  // Arabic description matches nothing here and passes through whole — the card
  // then repeats the prefix under its own "1 day pass" line. Cosmetic, never an
  // empty or wrong name, and no worse than the inline regexes this replaced. If
  // it is ever reported, strip on the localised `dayPassLabel` message rather
  // than growing this pattern per language.
  return raw.replace(/^(?:nol\s+)?day\s*pass\b\s*[-–—:]?\s*/i, "").trim() || raw;
};

/**
 * Booked offers holding a NOL pass that covers any of `legIds`.
 *
 * Cancellation selects offers by `offer.tripCoverage.coveredTripId`, and a NOL
 * offer has NO tripCoverage of its own — only its ADMISSION carries
 * `coveredLegIds`. So a cancelled booking refunded the train and silently left
 * the pass behind: the passenger loses the money and keeps a pass they did not
 * ask to keep. Both the refund QUOTE and the refund COMMIT must use this, or
 * they disagree about what is being refunded.
 */
export const nolOffersForLegs = (
  booking: { bookedOffers?: any[] } | undefined | null,
  legIds: Set<string>
): any[] =>
  (booking?.bookedOffers || []).filter((offer) =>
    (offer?.admissions || []).some(
      (adm: any) =>
        isNolAdmission(adm) &&
        (adm?.tripCoverage?.coveredLegIds || []).some((legId: string) => legIds.has(legId))
    )
  );

/**
 * The pass "unit" a booking is sold in — "" when it is not a pass booking.
 *
 * A NOL day pass carries `indicatedConsumption`
 * ({unit:{objectType:"TripTravelAccountUnit"},amount:1}, observed on INT)
 * exactly like a Travel Pass admission, and it lives on its OWN booked offer.
 * Reading `bookedOffers[0].admissions[0]` therefore calls an ordinary train
 * booking with a Nol attached a "pass booking" as soon as that offer sorts
 * first — which is why the misread came and went. Both callers ask through
 * here so the two verdicts cannot drift apart again.
 *
 * Positional otherwise: the FIRST offer holding an ordinary admission decides,
 * exactly as before, so nothing else changes about which bookings qualify.
 */
export const passObjectTypeOf = (bookedOffers: any[] | undefined): string => {
  const ordinaryAdmissionsOf = (offer: any) =>
    (offer?.admissions || []).filter((admission: any) => !isNolAdmission(admission));
  const firstPassOffer = (bookedOffers || []).find(
    (offer) => ordinaryAdmissionsOf(offer).length > 0
  );
  return firstPassOffer
    ? ordinaryAdmissionsOf(firstPassOffer)[0]?.indicatedConsumption?.unit?.objectType || ""
    : "";
};

/** One receipt line for NOL passes bought on a trip. */
export interface INolSummaryRow {
  /** Variant name off the booked product, e.g. "Gold". Empty if unnamed. */
  variant: string;
  count: number;
  amount: number;
  stopName?: string;
  isRefunded?: boolean;
}

/**
 * Receipt rows for the NOL passes attached to a trip's legs.
 *
 * Scanned across ALL booked offers, never a trip-filtered subset: a pass lands
 * on its OWN booked offer and that offer carries no
 * `tripCoverage.coveredTripId` (INT booking 7407fa55). The ADMISSION's
 * `coveredLegIds` is the only link back to the trip.
 *
 * Rows merge by variant + unit price, so two passengers on the same pass read
 * "2 Gold nol One-Day Pass" with the summed amount rather than two identical
 * lines — the same shape shuttle rows use.
 *
 * The variant comes off the booked offer's NOL product — the same field mobile
 * reads (AddonsHelper.nolPassVariant) — never a fixed "Nol Card" string, which
 * read the same for a Gold pass and a Silver one. Web additionally strips the
 * "Day Pass - " prefix (see [[nolVariantName]]); mobile does not, so the two
 * differ by that prefix until mobile follows.
 *
 * A REFUNDED pass still produces a row (the passenger paid for it once), and so
 * does a RELEASED one that was ISSUED — present in `booking.fulfillments`
 * (`issuedIds`). That is a paid pass cancelled for nothing with its
 * non-refundable ticket (ConfirmCancelClickAction.releaseWithoutRefund). Other
 * terminal statuses — including an unpaid RELEASED leftover — are dropped.
 */
export const nolSummaryRowsForTrip = (
  offers: any[] | undefined,
  tripLegIds: Set<string>,
  isTerminalStatus: (status: string) => boolean,
  toAmount: (price: any) => number,
  issuedIds?: Set<string>
): INolSummaryRow[] => {
  const rows = new Map<string, INolSummaryRow>();
  for (const offer of offers || []) {
    for (const admission of (offer?.admissions || [])) {
      if (!isNolAdmission(admission)) continue;
      const coversTrip = (admission?.tripCoverage?.coveredLegIds || []).some((legId: string) =>
        tripLegIds.has(legId)
      );
      if (!coversTrip) continue;
      const status = admission?.status || "";
      // Unlike the shuttle rule (REFUNDED_ADMISSION_STATUSES), RELEASED counts
      // only when the pass was issued: an unissued one is an unpaid leftover.
      const isRefunded =
        status === "REFUNDED" || (status === "RELEASED" && !!issuedIds?.has(admission?.id));
      if (isTerminalStatus(status) && !isRefunded) continue;
      const stopName = admission?.fmLmTravelPassStop || "";
      const variant = nolVariantName(nolPassProduct(offer?.products)?.description);
      const amount = toAmount(admission.price);
      const key = `${variant}|${amount}|${isRefunded}`;
      const existing = rows.get(key);
      if (existing) {
        existing.count += 1;
        existing.amount += amount;
      } else {
        rows.set(key, { variant, count: 1, amount, stopName, isRefunded });
      }
    }
  }
  return [...rows.values()];
};

/**
 * Does this booked pass belong to at least one of `passengerIds`?
 *
 * `undefined` means "don't scope" — every caller that deletes or lists a leg's
 * passes wholesale relies on that, and passing a scope is opt-in.
 *
 * FAIL SAFE, and deliberately the opposite of the unscoped default: once a
 * scope IS given, a pass whose owner cannot be established (`passengerIds`
 * absent or empty — the field is `nullable` in docs/retailer.json) is EXCLUDED.
 * Refunding a pass we cannot prove belongs to the selected passenger is exactly
 * ACPT-006; the same rule `ownedByExchangingPassenger` (sessionRefundLegs.ts)
 * applies on the change-date path, and the two must agree or a manual refund
 * and a change-date disagree about whose pass an admission is.
 */
const passBelongsToPassenger = (
  pass: { passengerIds?: string[] | null } | null | undefined,
  passengerIds?: ReadonlySet<string>
): boolean => {
  if (!passengerIds) return true;
  return (pass?.passengerIds || []).some((id) => passengerIds.has(id));
};

/**
 * Ids to pass to DELETE /bookings/{id}/fmlm-travel-passes?ids= for a leg.
 *
 * These are the booked admission ids, matching the sibling
 * `/bookings/{id}/admissions?ids=` convention. Only valid before
 * confirmation/fulfillment — a fulfilled pass must go through the refund flow.
 *
 * `passengerIds` narrows to the passes those BOOKING passengers hold. Omit it
 * for the whole leg (what every delete path wants); pass it on the refund path,
 * where an unscoped answer cancels passes belonging to passengers the user
 * never selected.
 */
export const extractBookedNolPassIds = (
  booking: IBookingResponse | null,
  legId: string,
  passengerIds?: ReadonlySet<string>
): string[] => {
  const offers = (booking?.booking as any)?.bookedOffers || [];
  const ids: string[] = [];
  for (const offer of offers) {
    for (const adm of offer?.admissions || []) {
      const onLeg = (adm?.tripCoverage?.coveredLegIds || []).includes(legId);
      if (adm?.id && onLeg && isNolAdmission(adm) && passBelongsToPassenger(adm, passengerIds)) {
        ids.push(adm.id);
      }
    }
  }
  return ids;
};

/**
 * Booked NOL ancillaries on a leg → {bookedOfferId, ancillaryId} for deletion.
 *
 * `passengerIds` narrows to those passengers' passes — see
 * [[extractBookedNolPassIds]].
 */
export const extractBookedNolAncillaries = (
  booking: IBookingResponse | null,
  legId: string,
  passengerIds?: ReadonlySet<string>
): { bookedOfferId: string; ancillaryId: string }[] => {
  const offers = (booking?.booking as any)?.bookedOffers || [];
  const out: { bookedOfferId: string; ancillaryId: string }[] = [];
  for (const offer of offers) {
    for (const anc of offer?.ancillaries || []) {
      const onLeg = (anc?.tripCoverage?.coveredLegIds || []).includes(legId);
      if (
        anc?.id &&
        onLeg &&
        isNolAncillary(offer?.products, anc) &&
        passBelongsToPassenger(anc, passengerIds)
      ) {
        out.push({ bookedOfferId: offer.offerId, ancillaryId: anc.id });
      }
    }
  }
  return out;
};

/**
 * The `{variant}` to feed the Nol row/chip messages (`e2eNolCardsInShort`,
 * `e2eNolCardsIn`).
 *
 * Those messages are count + name and carry NO noun of their own — the backend
 * description already names the product, so a message that appended "Nol card"
 * printed "1 Gold nol One-Day Pass Nol card". Which leaves them with nothing to
 * say when the variant is missing: `nolVariantName(undefined)` is `""` (a NOL
 * offer whose products carry no description), and an unguarded row rendered as
 * a bare "2" opposite a price.
 *
 * So the fallback lives here, once, rather than as a ternary at each of the
 * four call sites — the payment receipt, both cancel-summary rows, and the
 * booking-details chip. `nolCard` ("nol One-Day Pass") is our own chrome, so it
 * is translated; a real variant is backend copy and passes through as written.
 */
export const nolVariantOrFallback = (
  intl: { formatMessage: (descriptor: { id: string }) => string },
  variant: string | undefined | null
): string => (variant || "").trim() || intl.formatMessage({ id: "nolCard" });
